import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { mediaUrl } from '../api/client';
import { useImportMedia, useMedia, useUploadMedia } from '../api/queries';
import { errorMessage } from '../copy';
import { Dialog, dialogStyles } from './Dialog';
import { EMPTY_RIGHTS, RightsFields, declared } from './RightsFields';
import styles from './MediaPicker.module.css';
import ui from './ui.module.css';

/**
 * Picking a candidate's picture, the three ways the requirement allows: choose
 * one already stored, upload from disk, or paste a URL — which the **API**
 * fetches and copies, so what the poll ends up holding is always a local
 * `/media/...` path (02-requirements.md §Media Library).
 *
 * Upload and import both return the stored picture and hand it straight back to
 * the caller: an administrator who just uploaded a face meant to use it.
 */
export function MediaPicker({
  value,
  onPick,
  onClose,
}: {
  value?: string | null;
  onPick: (path: string) => void;
  onClose: () => void;
}): ReactNode {
  const { data: images = [], isLoading } = useMedia();
  const upload = useUploadMedia();
  const importUrl = useImportMedia();
  const fileRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rights, setRights] = useState(EMPTY_RIGHTS);
  const declaration = declared(rights);

  const take = (path: string): void => {
    onPick(path);
    onClose();
  };

  const onFile = (file: File | undefined): void => {
    if (!file || !declaration) return;
    setError(null);
    upload.mutate({ file, rights: declaration }, {
      onSuccess: (stored) => take(stored.path),
      onError: (cause) => setError(errorMessage(cause)),
    });
  };

  const onImport = (): void => {
    if (!url.trim() || !declaration) return;
    setError(null);
    importUrl.mutate({ url: url.trim(), rights: declaration }, {
      onSuccess: (stored) => take(stored.path),
      onError: (cause) => setError(errorMessage(cause)),
    });
  };

  const busy = upload.isPending || importUrl.isPending;

  return (
    <Dialog title="Escolher imagem" onClose={onClose} wide>
      {/* Uma imagem nova só entra com a base legal declarada. */}
      <RightsFields value={rights} onChange={setRights} idPrefix="picker" />
      <div className={styles.sources}>
        <button
          type="button"
          className={ui.ghost}
          disabled={busy || !declaration}
          onClick={() => fileRef.current?.click()}
        >
          {upload.isPending ? 'Enviando…' : 'Enviar do computador'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(event) => onFile(event.target.files?.[0])}
        />
        <input
          className={`${ui.input} ${styles.url}`}
          placeholder="https://… (o servidor baixa e guarda uma cópia)"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
        <button
          type="button"
          className={ui.ghost}
          onClick={onImport}
          disabled={busy || !url.trim() || !declaration}
        >
          {importUrl.isPending ? 'Baixando…' : 'Importar'}
        </button>
      </div>

      {error ? <p className={ui.error}>{error}</p> : null}

      {isLoading ? (
        <p className={ui.muted}>Carregando imagens…</p>
      ) : images.length === 0 ? (
        <p className={ui.muted}>Nenhuma imagem guardada ainda.</p>
      ) : (
        <div className={styles.grid}>
          {images.map((image) => (
            <button
              key={image.name}
              type="button"
              className={image.path === value ? `${styles.tile} ${styles.tileSelected}` : styles.tile}
              onClick={() => take(image.path)}
            >
              <img className={styles.image} src={mediaUrl(image.path)} alt={image.name} />
              <span className={styles.name}>{image.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className={dialogStyles.actions}>
        <button type="button" className={ui.ghost} onClick={onClose}>
          Cancelar
        </button>
      </div>
    </Dialog>
  );
}
